import { useState, useMemo, useCallback } from 'react';

export type SortOrder = 'asc' | 'desc';

export interface SortOption {
  value: string;
  label: string;
}

export interface UseDataTableOptions<T> {
  data: T[];
  searchFields?: (keyof T | ((item: T) => string))[];
  initialSortBy?: string;
  initialSortOrder?: SortOrder;
  sortOptions?: SortOption[];
  initialPageSize?: string;
  storageKey?: string;
}

// Pasos base predefinidos fuera del hook (cero recreación en memoria)
const BASE_PAGE_SIZES = [10, 25, 50, 100];

export function useDataTable<T extends Record<string, any>>({
  data,
  searchFields = [],
  initialSortBy = '',
  initialSortOrder = 'asc',
  sortOptions = [],
  initialPageSize = '10',
  storageKey,
}: UseDataTableOptions<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [page, setPage] = useState(1);

  const [pageSize, setPageSizeState] = useState<string>(() => {
    if (storageKey && typeof window !== 'undefined') {
      const saved = localStorage.getItem(storageKey);
      if (saved) return saved;
    }
    return initialPageSize;
  });

  const setPageSize = useCallback((newSize: string) => {
    setPageSizeState(newSize);
    setPage(1);
    if (storageKey && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, newSize);
    }
  }, [storageKey]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const toggleSortOrder = useCallback(() => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  // 1. Filtered data by search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim() || searchFields.length === 0) return data;
    const q = searchQuery.toLowerCase().trim();

    return data.filter((item) => {
      return searchFields.some((field) => {
        const val = typeof field === 'function' ? field(item) : item[field];
        if (val === null || val === undefined) return false;
        return String(val).toLowerCase().includes(q);
      });
    });
  }, [data, searchQuery, searchFields]);

  // 2. Sorted data
  const sortedData = useMemo(() => {
    if (!sortBy) return filteredData;

    return [...filteredData].sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      if (valA === null || valA === undefined) valA = '';
      if (valB === null || valB === undefined) valB = '';

      let cmp = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        cmp = valA - valB;
      } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        cmp = (valA ? 1 : 0) - (valB ? 1 : 0);
      } else {
        cmp = String(valA).localeCompare(String(valB), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }

      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortBy, sortOrder]);

  // 3. Paginación y Totales
  const totalCount = sortedData.length;
  const isAllPages = pageSize === 'all' || pageSize === '0';

  const effectivePageSize = isAllPages ? Math.max(totalCount, 1) : parseInt(pageSize, 10) || 10;
  const totalPages = Math.ceil(totalCount / effectivePageSize) || 1;
  const currentPage = Math.min(page, totalPages);

  const paginatedData = useMemo(() => {
    if (isAllPages) return sortedData;
    const start = (currentPage - 1) * effectivePageSize;
    return sortedData.slice(start, start + effectivePageSize);
  }, [sortedData, isAllPages, currentPage, effectivePageSize]);

  // 4. Generación Declarativa y Optimizada de Tamaños de Página
  const pageSizeOptions = useMemo(() => {
    // Tomar solo los escalones necesarios basados en el total
    const validSizes = BASE_PAGE_SIZES.filter((size, index) => {
      if (index === 0) return true; // Siempre incluir el primer nivel (10)
      return totalCount > BASE_PAGE_SIZES[index - 1]; // Incluir el siguiente escalón si superamos el anterior
    });

    const options = validSizes.map((size) => ({
      value: String(size),
      label: `${size}/pág.`,
    }));

    options.push({ value: 'all', label: `Todos (${totalCount})` });

    return options;
  }, [totalCount]);

  // Validar opción activa
  const activePageSize = useMemo(() => {
    if (pageSize === 'all') return 'all';
    const exists = pageSizeOptions.some((opt) => opt.value === pageSize);
    return exists ? pageSize : pageSizeOptions[0].value;
  }, [pageSize, pageSizeOptions]);

  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * effectivePageSize + 1;
  const endIndex = Math.min(currentPage * effectivePageSize, totalCount);

  return {
    searchQuery,
    setSearchQuery: handleSearchChange,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    toggleSortOrder,
    sortOptions,
    page: currentPage,
    setPage,
    pageSize: String(activePageSize),
    setPageSize,
    pageSizeOptions,
    filteredData,
    sortedData,
    paginatedData,
    totalCount,
    totalPages,
    startIndex,
    endIndex,
  };
}